import { all, PutEffect, takeEvery, put, call, CallEffect, select, SelectEffect, AllEffect } from 'redux-saga/effects';
import { ContractTransaction, ContractReceipt, providers } from 'ethers';
import { TransactionReceipt, TransactionResponse } from '@ethersproject/abstract-provider';

import { addPendingTransaction, addTransaction, checkPendingTransactions, setCompletedTransaction } from './actions';
import { addErrorNotification } from '../../services/notifications';
import { getTransaction, getTransactionsData } from './selectors';
import { CompletedTransaction, MappedTransactions } from './slice';

export function* processPendingTransaction({
  payload: { tx, summary, completeMessage, errorMessage },
}: ReturnType<typeof addPendingTransaction>): Generator<PutEffect | CallEffect, void, void> {
  const txPayload = {
    hash: tx.hash,
    from: tx.from,
    chainId: tx.chainId,
    addedTime: Date.now(),
    summary,
    completeMessage,
    errorMessage,
  };
  yield put(addTransaction(txPayload));
  yield call(waitContractTransaction, tx);
}

function* waitContractTransaction(
  tx: ContractTransaction | TransactionResponse,
): Generator<CallEffect | SelectEffect | PutEffect, void, CompletedTransaction & ContractReceipt> {
  const result = yield call(tx.wait, 1);
  const txInfo = yield select(getTransaction, tx.hash);

  if (result.status === 0) {
    addErrorNotification(tx.hash, 'Failed transaction', txInfo.errorMessage || '');
  }

  yield put(
    setCompletedTransaction({
      ...txInfo,
      status: result.status === 0 ? 0 : 1,
      confirmedTime: Date.now(), // result.timestamp?
    }),
  );
}

function* waitTransaction(
  txHash: string,
  provider: providers.Web3Provider,
): Generator<CallEffect | Promise<TransactionResponse>, void, ContractReceipt & TransactionResponse> {
  try {
    const tx = yield provider.getTransaction(txHash);
    if (tx) {
      yield call(waitContractTransaction, tx);
    } else {
      console.warn(`Pending transaction ${txHash} not found on network.`);
    }
  } catch (e) {
    console.error(`Error while trying to wait for transaction: ${e.message}`);
  }
}

/**
 * Check if any previously saved pending transaction has
 * been processed and removes them from pending.
 */
export function* checkCachedPendingTransactions({
  payload: provider,
}: ReturnType<typeof checkPendingTransactions>): Generator<
  | SelectEffect
  | PutEffect
  | CallEffect
  | AllEffect<
      Promise<TransactionReceipt> | undefined | CallEffect | PutEffect<ReturnType<typeof setCompletedTransaction>>
    >,
  void,
  MappedTransactions & (TransactionReceipt | undefined)[]
> {
  const savedTransactions = yield select(getTransactionsData);
  const transactionsToBeChecked: CompletedTransaction[] = [];
  const promises = Object.values(savedTransactions).map((tx) => {
    if (tx && tx.status === undefined) {
      transactionsToBeChecked.push(tx);
      return provider.getTransactionReceipt(tx.hash);
    }
  });
  const transactions: (TransactionReceipt | undefined)[] = yield all(promises);
  yield all(
    transactions.map((tx, index) => {
      if (tx) {
        // transactions have been confirmed meanwhile, update their status
        return put(
          setCompletedTransaction({
            ...savedTransactions[tx.transactionHash],
            status: tx.status,
            confirmedTime: Date.now(), // TODO: read block time or remove this
          }),
        );
      } else {
        // start waiting for the transaction to be mined
        if (transactionsToBeChecked[index]) {
          return call(waitTransaction, transactionsToBeChecked[index].hash, provider);
        }
      }
    }),
  );
}

export function* transactionsSagaWatcher(): Generator {
  yield all([
    takeEvery(addPendingTransaction, processPendingTransaction),
    takeEvery(checkPendingTransactions, checkCachedPendingTransactions),
  ]);
}
